import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  ClipboardCheck,
  Wallet,
  Archive,
  ArrowRight,
  Clock,
  Users,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  Calendar,
  TrendingUp
} from 'lucide-react';

// --- TYPES ---
interface DashboardStats {
  pendingSheets: number;
  validatedThisMonth: number;
  totalEmployees: number;
  totalHoursThisMonth: number;
}

export default function SecretariatDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    pendingSheets: 0,
    validatedThisMonth: 0,
    totalEmployees: 0,
    totalHoursThisMonth: 0
  });
  const [loading, setLoading] = useState(true);

  // Charger les statistiques
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

        // Feuilles en attente
        const { count: pendingCount } = await supabase
          .from('weekly_sheets')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');

        // Feuilles validées ce mois
        const { count: validatedCount } = await supabase
          .from('weekly_sheets')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'validated')
          .gte('validated_at', startOfMonth)
          .lte('validated_at', endOfMonth + 'T23:59:59');

        // Nombre d'employés
        const { count: employeesCount } = await supabase
          .from('employees')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true);

        // Heures totales ce mois (feuilles validées)
        const { data: hoursData } = await supabase
          .from('time_entries')
          .select(`
            hours_normal,
            hours_saturday,
            hours_major,
            weekly_sheets!inner(status)
          `)
          .gte('work_date', startOfMonth)
          .lte('work_date', endOfMonth)
          .eq('weekly_sheets.status', 'validated');

        const totalHours = hoursData?.reduce((acc, entry) => {
          return acc + (entry.hours_normal || 0) + (entry.hours_saturday || 0) + (entry.hours_major || 0);
        }, 0) || 0;

        setStats({
          pendingSheets: pendingCount || 0,
          validatedThisMonth: validatedCount || 0,
          totalEmployees: employeesCount || 0,
          totalHoursThisMonth: totalHours
        });
      } catch (error) {
        console.error('Erreur chargement stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const cards = [
    {
      title: 'Validation Heures',
      subtitle: 'Contrôle Hebdomadaire',
      icon: <ClipboardCheck size={24} />,
      desc: "Vérifier et valider les feuilles d'heures des équipes.",
      path: '/secretariat/validate',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      lightBg: '#fef3c7',
      color: '#d97706',
      badge: stats.pendingSheets > 0 ? stats.pendingSheets : null,
      badgeColor: '#ea580c'
    },
    {
      title: 'Gestion Paie',
      subtitle: 'Calculs & Exports',
      icon: <Wallet size={24} />,
      desc: "Tableaux consolidés : Heures, Trajets, Transports et Paniers.",
      path: '/secretariat/payroll',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      lightBg: '#eef2ff',
      color: '#4f46e5',
      badge: null,
      badgeColor: null
    },
    {
      title: 'Gestion Congés',
      subtitle: 'Absences & Formations',
      icon: <Calendar size={24} />,
      desc: "Saisie et suivi des demandes de congés, maladies et formations.",
      path: '/secretariat/leaves',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      lightBg: '#f3e8ff',
      color: '#7c3aed',
      badge: null,
      badgeColor: null
    },
    {
      title: 'Archives',
      subtitle: 'Documents & GED',
      icon: <Archive size={24} />,
      desc: "Accès centralisé à l'historique des documents administratifs.",
      path: '/secretariat/documents',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      lightBg: '#e0f2fe',
      color: '#0284c7',
      badge: null,
      badgeColor: null
    },
    {
      title: 'Mes Heures',
      subtitle: 'Vendredis & Extras',
      icon: <Clock size={24} />,
      desc: "Déclarer les vendredis travaillés en période de forte activité.",
      path: '/secretariat/hours',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
      lightBg: '#fff7ed',
      color: '#ea580c',
      badge: null,
      badgeColor: null
    }
  ];

  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* HEADER */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '20px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' }}>
              Espace Secretariat
            </h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
              Pilotage administratif et comptable
            </p>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#f8fafc', padding: '10px 16px',
            borderRadius: '12px', color: '#475569', fontSize: '0.9rem'
          }}>
            <Calendar size={18} color="#64748b" />
            <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{formattedDate}</span>
          </div>
        </div>
      </div>

      <div>

        {/* STATISTIQUES */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>

          {/* En attente */}
          <div style={{
            background: stats.pendingSheets > 0
              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
              : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: '16px',
            padding: '20px',
            border: stats.pendingSheets > 0 ? '1px solid #fde68a' : '1px solid #bbf7d0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                padding: '10px',
                background: stats.pendingSheets > 0 ? '#fbbf24' : '#22c55e',
                borderRadius: '10px',
                color: 'white'
              }}>
                {stats.pendingSheets > 0 ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: stats.pendingSheets > 0 ? '#b45309' : '#15803d',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                En attente
              </span>
            </div>
            <div style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              color: stats.pendingSheets > 0 ? '#92400e' : '#166534'
            }}>
              {loading ? '-' : stats.pendingSheets}
            </div>
            <div style={{
              fontSize: '0.8rem',
              color: stats.pendingSheets > 0 ? '#a16207' : '#16a34a',
              marginTop: '4px'
            }}>
              {stats.pendingSheets > 0 ? 'feuille(s) à valider' : 'Tout est à jour'}
            </div>
          </div>

          {/* Validées ce mois */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                padding: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '10px',
                color: 'white'
              }}>
                <FileCheck size={20} />
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Validées (mois)
              </span>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
              {loading ? '-' : stats.validatedThisMonth}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>
              feuilles ce mois
            </div>
          </div>

          {/* Employés actifs */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                padding: '10px',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                borderRadius: '10px',
                color: 'white'
              }}>
                <Users size={20} />
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Employés actifs
              </span>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
              {loading ? '-' : stats.totalEmployees}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>
              collaborateurs
            </div>
          </div>

          {/* Heures ce mois */}
          <div style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white',
            boxShadow: '0 4px 6px rgba(79, 70, 229, 0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                padding: '10px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '10px'
              }}>
                <Clock size={20} />
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                opacity: 0.9,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Heures validées
              </span>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
              {loading ? '-' : stats.totalHoursThisMonth.toFixed(0)}
            </div>
            <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '4px' }}>
              heures ce mois
            </div>
          </div>
        </div>

        {/* ACTIONS RAPIDES */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#475569',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <TrendingUp size={18} color="#64748b" />
            Actions rapides
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {cards.map((card, idx) => (
              <button
                key={idx}
                onClick={() => navigate(card.path)}
                style={{
                  textAlign: 'left',
                  background: 'white',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px -8px rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.borderColor = card.color;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                {/* Badge notification */}
                {card.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: card.badgeColor,
                    color: 'white',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.4)'
                  }}>
                    {card.badge}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                  <div style={{
                    background: card.gradient,
                    padding: '14px',
                    borderRadius: '12px',
                    color: 'white',
                    boxShadow: `0 4px 8px -2px ${card.color}40`
                  }}>
                    {card.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: '#1e293b',
                      margin: '0 0 4px 0'
                    }}>
                      {card.title}
                    </h3>
                    <div style={{
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {card.subtitle}
                    </div>
                  </div>
                </div>

                <p style={{
                  color: '#64748b',
                  fontSize: '0.9rem',
                  lineHeight: '1.5',
                  margin: '0 0 16px 0'
                }}>
                  {card.desc}
                </p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: card.color,
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>
                  Accéder
                  <ArrowRight size={16} />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* QUICK TIPS */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px 24px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            padding: '12px',
            background: '#eff6ff',
            borderRadius: '12px',
            color: '#2563eb'
          }}>
            <Calendar size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '2px' }}>
              Rappel
            </div>
            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
              Les feuilles d'heures doivent être validées avant la clôture de paie mensuelle.
              Pensez à vérifier les zones de déplacement et les indemnités.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
